import React, { useState, useEffect, useRef } from "react";

import {
  X,
  ChevronLeft,
  ChevronRight,
  Camera,
  Home,
  ArrowLeft,
} from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Keyboard, Virtual } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";

import theme from "./theme";
import { useNavigate, Link } from "react-router-dom";

import Footer from "./components/footer";

import { useTranslation } from "react-i18next";

// Load images using require.context
const marroneImages = require.context("./images/rooms/marrone", true);
const marroneList = marroneImages.keys().map((image) => marroneImages(image));

const azzurraImages = require.context("./images/rooms/azzurra", true);
const azzurraList = azzurraImages.keys().map((image) => azzurraImages(image));

const rosaImages = require.context("./images/rooms/rosa", true);
const rosaList = rosaImages.keys().map((image) => rosaImages(image));

const verdeImages = require.context("./images/rooms/verde", true);
const verdeList = verdeImages.keys().map((image) => verdeImages(image));

// Helper: sort filenames containing numbers in ascending numeric order
const numericSort = (a, b) => {
  const extractNumber = (str) => {
    const match = str.match(/(\d+)/);
    return match ? parseInt(match[1], 10) : 0;
  };
  return extractNumber(a) - extractNumber(b);
};
const galleryImages = require.context("./images/gallery", true);
const galleryList = galleryImages
  .keys()
  .sort(numericSort)
  .map((image) => galleryImages(image));

// Custom styles
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=Lora:wght@400;500&family=Open+Sans:wght@300;400;500&display=swap');

  :root {
    --sage: ${theme.sage};
    --cream: ${theme.cream};
    --dusty-pink: ${theme.dustyPink};
    --warm-brown: ${theme.warmBrown};
    --light-sage: ${theme.lightSage};
    --wheat: ${theme.wheat};
    --moss: ${theme.moss};
    --blush: ${theme.blush};
  }

  body {
    background-color: var(--cream);
    font-family: 'Open Sans', sans-serif;
  }

  h1, h2, h3 {
    font-family: 'Playfair Display', serif;
  }

  .font-lora {
    font-family: 'Lora', serif;
  }

  /* Hanging sign used for section headings */
  .hanging-sign {
    display: inline-block;
    padding: 2rem 3rem;
    border: 4px solid var(--warm-brown);
    background-color: var(--cream);
    box-shadow: 0 4px 6px rgba(0,0,0,0.1);
    position: relative;
  }
  .hanging-sign::before,
  .hanging-sign::after {
    content: '';
    position: absolute;
    top: -100px;
    width: 10px;
    height: 100px;
    background-color: var(--warm-brown);
  }
  .hanging-sign::before { left: 20%; }
  .hanging-sign::after  { right: 20%; }
`;

// Create gallery data from image lists
const createGalleryData = () => {
  const allImages = [];
  let id = 1;

  galleryList.forEach((src) => {
    allImages.push({
      id: id++,
      src,
      category: "Gallery",
    });
  });

  marroneList.forEach((src) => {
    allImages.push({
      id: id++,
      src,
      category: "Stanza Marrone",
    });
  });

  azzurraList.forEach((src) => {
    allImages.push({
      id: id++,
      src,
      category: "Stanza Azzurra",
    });
  });

  rosaList.forEach((src) => {
    allImages.push({
      id: id++,
      src,
      category: "Stanza Rosa",
    });
  });

  verdeList.forEach((src) => {
    allImages.push({
      id: id++,
      src,
      category: "Stanza Verde",
    });
  });

  return allImages;
};

const galleryData = createGalleryData();

// Section titles mapping
const sectionTitles = {
  "Stanza Marrone": { title: "gallerySectionBrownRoom", icon: Home },
  "Stanza Azzurra": { title: "gallerySectionCyanRoom", icon: Home },
  "Stanza Rosa": { title: "gallerySectionPinkRoom", icon: Home },
  "Stanza Verde": { title: "gallerySectionGreenRoom", icon: Home },
  Gallery: { title: "gallerySectionGallery", icon: Camera },
};

// SimpleGallery with centered thumbnails
const SimpleGallery = ({ onClose = () => {}, imgs, initialIndex = 0 }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const thumbnailContainerRef = useRef(null);
  const thumbnailRefs = useRef([]);
  const swiperRef = useRef(null);
  const { t } = useTranslation();
  // Detect iOS devices to disable heavy 3‑D effects
  const isIOS =
    typeof navigator !== "undefined" &&
    /iPhone|iPad|iPod/.test(navigator.userAgent);

  // Center the active thumbnail when currentIndex changes
  useEffect(() => {
    if (thumbnailContainerRef.current && thumbnailRefs.current[currentIndex]) {
      const container = thumbnailContainerRef.current;
      const activeThumbnail = thumbnailRefs.current[currentIndex];

      const containerWidth = container.offsetWidth;
      const thumbnailLeft = activeThumbnail.offsetLeft;
      const thumbnailWidth = activeThumbnail.offsetWidth;

      const scrollLeft =
        thumbnailLeft - containerWidth / 2 + thumbnailWidth / 2;

      container.scrollTo({
        left: scrollLeft,
        behavior: "smooth",
      });
    }
  }, [currentIndex]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [currentIndex]);

  const goToImage = (index) => {
    if (swiperRef.current) {
      swiperRef.current.slideTo(index);
    }
  };

  const currentImage = imgs[currentIndex];
  const displayTitle =
    sectionTitles[currentImage.category]?.title || currentImage.category;

  return (
    <div className="fixed inset-0 bg-black/95 z-50 flex flex-col">
      {/* Top controls */}
      <div className="absolute top-0 left-0 right-0 z-10 flex justify-between items-center p-4">
        <div className="flex items-center gap-3">
          <div className="bg-black/50 text-white px-4 py-2 rounded-full text-sm font-medium">
            {currentIndex + 1} / {imgs.length}
          </div>
          <div className="bg-black/70 text-white px-6 py-3 rounded-full text-lg font-semibold tracking-wide">
            {t(displayTitle)}
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        >
          <X size={24} className="text-white" />
        </button>
      </div>

      {/* Navigation arrows */}
      <button className="swiper-button-prev-custom absolute left-6 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 hover:bg-white transition-colors shadow-lg z-10">
        <ChevronLeft size={36} className="text-black" />
      </button>

      <button className="swiper-button-next-custom absolute right-6 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 hover:bg-white transition-colors shadow-lg z-10">
        <ChevronRight size={36} className="text-black" />
      </button>

      {/* Main image Swiper */}
      <div className="flex-1 flex items-center justify-center p-4 pb-24">
        <Swiper
          className="h-full w-full"
          modules={[Navigation, Keyboard, Virtual]}
          effect={isIOS ? "slide" : "coverflow"}
          coverflowEffect={{
            rotate: 50,
            stretch: 0,
            depth: 100,
            modifier: 1,
            slideShadows: !isIOS,
          }}
          centeredSlides={true}
          slidesPerView={1}
          navigation={{
            nextEl: ".swiper-button-next-custom",
            prevEl: ".swiper-button-prev-custom",
          }}
          keyboard={{ enabled: true }}
          preloadImages={false}
          spaceBetween={30}
          virtual={{ addSlidesBefore: 1, addSlidesAfter: 1 }}
          initialSlide={initialIndex}
          onSwiper={(swiper) => (swiperRef.current = swiper)}
          onSlideChange={(swiper) => setCurrentIndex(swiper.realIndex)}
          loop={false}
        >
          {imgs.map((image, index) => (
            <SwiperSlide
              key={index}
              virtualIndex={index}
              className="relative flex items-center justify-center h-full"
            >
              <img
                src={typeof image === "string" ? image : image.src}
                alt={
                  typeof image === "object" ? image.alt : `Image ${index + 1}`
                }
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 max-w-4xl max-h-full object-contain w-full"
                loading="lazy"
                decoding="async"
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* Bottom thumbnail strip with centering */}
      <div className="absolute bottom-0 left-0 right-0 bg-black/80 backdrop-blur-sm p-4">
        <div
          ref={thumbnailContainerRef}
          className="flex items-center gap-2 p-2 overflow-x-auto max-w-full"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {imgs.map((image, index) => {
            const isActive = index === currentIndex;
            return (
              <button
                key={index}
                ref={(el) => (thumbnailRefs.current[index] = el)}
                onClick={() => goToImage(index)}
                className={`flex-shrink-0 rounded-lg overflow-hidden transition-all duration-200 ${
                  isActive
                    ? "ring-2 ring-white"
                    : "hover:ring-1 hover:ring-white/50"
                }`}
                style={{
                  width: "60px",
                  height: "45px",
                  opacity: isActive ? 1 : 0.7,
                }}
              >
                <img
                  src={typeof image === "string" ? image : image.src}
                  alt={
                    typeof image === "object"
                      ? image.alt
                      : `Thumbnail ${index + 1}`
                  }
                  className="w-full h-full object-cover"
                />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Gallery Image Component
const GalleryImage = ({ image, onClick }) => {
  return (
    <div
      className="relative group cursor-pointer overflow-hidden rounded-lg bg-gray-200"
      onClick={() => onClick(image)}
      style={{ aspectRatio: "4/3" }}
    >
      <img
        src={image.src}
        alt="Gallery image"
        className="w-full h-full object-cover transition-all duration-300 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
        <div className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300 opacity-0 group-hover:opacity-100">
          <div className="bg-white/20 backdrop-blur-sm rounded-full p-3">
            <Camera size={24} className="text-white" />
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Gallery Component
const Gallery = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const slugify = (str) => str.toLowerCase().replace(/[^\w]+/g, "-");

  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [galleryStartIndex, setGalleryStartIndex] = useState(0);

  // Group images by category
  const imagesByCategory = galleryData.reduce((acc, image) => {
    if (!acc[image.category]) {
      acc[image.category] = [];
    }
    acc[image.category].push(image);
    return acc;
  }, {});

  const reorderedCategories = [
    "Gallery",
    ...Object.keys(imagesByCategory).filter((cat) => cat !== "Gallery"),
  ];

  const [activeCategory, setActiveCategory] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      const sectionElements = reorderedCategories.map((category) => ({
        element: document.getElementById(
          slugify(sectionTitles[category]?.title || category)
        ),
        category: category,
      }));

      const scrollY = window.scrollY + 200;

      let foundActive = null;
      for (let { element, category } of sectionElements) {
        if (
          element &&
          element.offsetTop <= scrollY &&
          element.offsetTop + element.offsetHeight > scrollY
        ) {
          foundActive = category;
          break;
        }
      }

      if (foundActive) {
        setActiveCategory(foundActive);
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [reorderedCategories]);

  const openGallery = (image) => {
    const imageIndex = galleryData.findIndex((img) => img.id === image.id);
    setGalleryStartIndex(imageIndex);
    setIsGalleryOpen(true);
  };

  const closeGallery = () => {
    setIsGalleryOpen(false);
  };

  const handleCategoryClick = (category) => {
    const sectionTitle = sectionTitles[category]?.title || category;
    const element = document.getElementById(slugify(sectionTitle));
    if (element) {
      const headerOffset = 180;
      const elementPosition = element.offsetTop - headerOffset;
      window.scrollTo({
        top: elementPosition,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.cream }}>
      <style>{styles}</style>
      {/* Header */}
      <header
        className="fixed top-0 w-full bg-white/90 backdrop-blur-sm shadow-sm z-50"
        style={{ borderBottom: `1px solid ${theme.wheat}` }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/")}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                style={{ color: theme.warmBrown }}
              >
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1
                  className="text-2xl font-bold"
                  style={{ color: theme.moss }}
                >
                  B&B Ligo
                </h1>
                <p className="text-xs" style={{ color: theme.warmBrown }}>
                  {t("bnbTagline")}
                </p>
              </div>
            </div>
            <Link
              to="/#contatti"
              reloadDocument
              className="px-4 py-2 rounded-full text-sm font-medium shadow-sm whitespace-nowrap"
                style={{
                  backgroundColor: theme.sage,
                  color: "white",
                }}
              >
                {t("Prenota")}
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-20 pb-12" style={{ backgroundColor: theme.blush }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1
            className="text-4xl sm:text-5xl font-bold mb-6"
            style={{ color: theme.moss }}
          >
            {t("galleryHeroTitle")}
          </h1>
          <p
            className="text-xl max-w-3xl mx-auto"
            style={{ color: theme.warmBrown }}
          >
            {t("galleryHeroSubtitle")}
          </p>
        </div>
      </section>

      <section
        className="py-4 shadow-sm sticky top-[64px] z-40"
        style={{
          backgroundColor: theme.cream,
          borderBottom: `1px solid ${theme.sage}40`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 flex flex-wrap gap-3 overflow-x-auto">
          {reorderedCategories.map((category) => (
            <button
              key={category}
              onClick={() => handleCategoryClick(category)}
              className={`px-3 py-2 rounded-full text-sm font-medium border-2 transition-all duration-300`}
              style={{
                borderColor:
                  activeCategory === category
                    ? theme.sage
                    : theme.cream,
                color: activeCategory === category ? theme.sage : theme.warmBrown,
              }}
            >
              {t(sectionTitles[category]?.title || category)}
            </button>
          ))}
        </div>
      </section>

      {/* Gallery Sections */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {reorderedCategories.map((category) => {
            const images = imagesByCategory[category];
            const sectionInfo = sectionTitles[category] || {
              title: category,
              icon: Camera,
            };
            return (
              <div
                key={category}
                id={slugify(sectionInfo.title)}
                className="mb-12"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div
                    className="p-2 rounded-full"
                    style={{ backgroundColor: theme.sage }}
                  >
                    <sectionInfo.icon size={20} className="text-white" />
                  </div>
                  <h2
                    className="text-2xl font-bold"
                    style={{ color: theme.moss }}
                  >
                    {t(sectionInfo.title)}
                  </h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {images.map((image) => (
                    <GalleryImage
                      key={image.id}
                      image={image}
                      onClick={openGallery}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <Footer />

      {/* SimpleGallery with centered thumbnails */}
      {isGalleryOpen && (
        <SimpleGallery
          onClose={closeGallery}
          imgs={galleryData}
          initialIndex={galleryStartIndex}
        />
      )}
    </div>
  );
};

export default Gallery;
